<template>
    <div class="control-panel">
        <div class="main-body">
            <div class="tabs-bar" ref="itemsBar">
                <ul class="list">
                    <li class="no-sublist" :class="{active: activeTabId===documentOverviewTabId}">
                        <div class="button" @click="setActiveTabId(documentOverviewTabId)">
                            <div class="button-content"><span>General Information</span></div>
                        </div>
                    </li>

                    <template v-if="enableSerial">
                        <li class="no-sublist" :class="{active: activeTabId===serialManagerTabId}">
                            <div class="button more-dropdown-button-container"
                                 @click="setActiveTabId(serialManagerTabId)">
                                <div class="button-content">
                                    <span>Serial Manager</span>
                                </div>
                                <simple-dropdown class="more-dropdown-button" v-if="inEditWorkMode">
                                    <template v-slot:dropdownMenu>
                                        <ul>
                                            <li>
                                                <button @click="createSerialAndBind">
                                                    <span>Create New Serial and Bind</span>
                                                </button>
                                            </li>
                                        </ul>
                                    </template>
                                </simple-dropdown>
                            </div>
                        </li>

                        <li class="divider" v-if="serialObjects.length"></li>

                        <li class="has-sublist" v-for="(serialObject,index) in serialObjects"
                            :key="serialObject['serial_id']"
                            :class="{active: activeTabId===serialObject['serial_id']}">
                            <div class="sublist-header sticky-header py-0">
                                <div class="button"
                                     @click="setActiveTabId(serialObject['serial_id'])">
                                    <div class="button-content">
                                        <span>Serial {{ index + 1 }}</span><span
                                        class="ml-1 comment"
                                        :title="serialObject['serial_id']">{{ serialObject['serial_id'] }}</span>
                                    </div>
                                    <button class="add-button" @click.stop="vAppendEventToSerial(serialObject)"
                                            v-if="inEditWorkMode"
                                            title="Add an Event to the Serial"><i class="gf gf-plus-circle"></i>
                                    </button>
                                </div>
                            </div>
                            <ul class="sublist">
                                <li v-for="(eventObject,index) in serialObject['events']||[]"
                                    :key="eventObject['_v_key']"
                                    class="no-sublist"
                                    :class="{active: activeTabId===eventObject['_v_key']}">
                                    <div class="button more-dropdown-button-container"
                                         @click="setActiveTabId(eventObject['_v_key'])">
                                        <div class="button-content">
                                            <span>Event {{ index + 1 }}
                                            </span>
                                            <span class="new-case-indicator"
                                                  v-if="eventNewlyCreated(eventObject)"></span>
                                          <span v-else class="ml-1 comment"
                                                :title="eventObject['event_id']">{{ eventObject['event_id'] }}</span>
                                        </div>
                                        <simple-dropdown class="more-dropdown-button" v-if="inEditWorkMode">
                                            <template v-slot:dropdownMenu>
                                                <ul>
                                                    <li>
                                                        <button @click="copyEventValues(eventObject['event_id'])">
                                                            Copy Event Values
                                                        </button>
                                                    </li>
                                                    <li>
                                                        <button @click="overwriteEventValues(eventObject['event_id'])">
                                                            Paste Event Values
                                                        </button>
                                                    </li>
                                                    <li class="separator"></li>
                                                    <template v-if="!eventNewlyCreated(eventObject)">
                                                        <li>
                                                            <button @click="vResetEventToLastSaved(eventObject)">Reset
                                                                Event
                                                                to
                                                                Last Saved
                                                            </button>
                                                        </li>
                                                        <li class="separator"></li>
                                                    </template>
                                                    <li>
                                                        <button @click="onDeleteEventButtonClicked(eventObject['_v_key'], serialObject)">
                                                            <span>Delete Event</span>
                                                        </button>
                                                    </li>
                                                </ul>
                                            </template>
                                        </simple-dropdown>
                                    </div>
                                </li>
                            </ul>
                        </li>
                    </template>
                    <template v-else>
                        <li class="divider mb-0"></li>
                        <li class="sticky-header">
                            <div class="section-header mb-0">
                                <span class="header">Events</span>
                                <button v-if="inEditWorkMode && !(maxEvents && eventObjects.length >= maxEvents)" class="add-button" @click="vAppendEventToEvents"
                                        title="Add an Event"><i class="gf gf-plus-circle"></i>
                                </button>
                            </div>
                        </li>
                        <li v-for="(eventObject,index) in eventObjects" :key="eventObject['_v_key']"
                            class="no-sublist"
                            :class="{active: activeTabId===eventObject['_v_key']}">
                            <div class="button more-dropdown-button-container"
                                 @click="setActiveTabId(eventObject['_v_key'])">
                                <div class="button-content">
                                    <span>Event {{ index + 1 }}</span>
                                    <span class="new-case-indicator" v-if="eventNewlyCreated(eventObject)"></span>
                                  <span v-else class="ml-1 comment"
                                        :title="eventObject['event_id']">{{ eventObject['event_id'] }}</span>
                                </div>
                                <simple-dropdown v-if="inEditWorkMode" class="more-dropdown-button">
                                    <template v-slot:dropdownMenu>
                                        <ul>
                                            <li>
                                                <button @click="copyEventValues(eventObject['event_id'])">
                                                    Copy Event Values
                                                </button>
                                            </li>
                                            <li>
                                                <button @click="overwriteEventValues(eventObject['event_id'])">
                                                    Paste Event Values
                                                </button>
                                            </li>
                                            <li class="separator"></li>
                                            <li v-if="!eventNewlyCreated(eventObject)">
                                                <button @click="vResetEventToLastSaved(eventObject)">Reset Event to
                                                    Last Saved
                                                </button>
                                            </li>

                                            <li>
                                                <button @click="onDeleteEventButtonClicked(eventObject['_v_key'], null)">
                                                    <span>Delete Event</span>
                                                </button>
                                            </li>
                                        </ul>
                                    </template>
                                </simple-dropdown>
                            </div>
                        </li>
                    </template>
                </ul>
            </div>
            <div class="tab-content-container" ref="tabContentContainer">
                <d-t-overview :document-description-items="documentDescriptionItems"
                              :document-id="documentObject['document_id']"
                              v-if="activeTabId===documentOverviewTabId"></d-t-overview>
                <template v-if="enableSerial">
                    <d-t-serial-manager v-show="activeTabId===serialManagerTabId" ref="serialManager"
                                        :parent-document-object="documentObject"
                                        :can-edit="canEdit"
                                        @createSerialAndBind="createSerialAndBind"
                                        @deleteSerial="attemptToDeleteSerial"></d-t-serial-manager>
                    <d-t-serial v-for="(serialObject) in serialObjects" v-show="activeTabId===serialObject['serial_id']"
                                :serial-object="serialObject"
                                :key="serialObject['serial_id']"
                                :can-edit="canEdit"
                                :parent-document-object="documentObject"
                                ref="dtSerials"
                                @viewInSerialManager="viewInSerialManager"
                                @appendEvent="vAppendEventToSerial"
                    ></d-t-serial>
                </template>
                <template v-if="isCapacityUpdater">
                    <d-t-event-for-c-u v-for="(eventObject) in eventObjects"
                                       v-show="activeTabId===eventObject['_v_key']"
                                       :key="eventObject['_v_key']" :event-object="eventObject"
                                       :document-object="documentObject"
                                       @valueChanged="valueChanged"
                                       ref="eventForms"
                    ></d-t-event-for-c-u>
                </template>
                <template v-else-if="isAnnualReport">
                    <d-t-event-for-a-r v-for="(eventObject) in eventObjects"
                                       v-show="activeTabId===eventObject['_v_key']"
                                       :key="eventObject['_v_key']" :event-object="eventObject"
                                       :document-object="documentObject"
                                       @valueChanged="valueChanged"
                                       ref="eventForms"
                    ></d-t-event-for-a-r>
                </template>
                <template v-else>
                    <d-t-event v-for="(eventObject) in eventObjects" v-show="activeTabId===eventObject['_v_key']"
                               :key="eventObject['_v_key']" :event-object="eventObject"
                               :fields-template="projectFieldsTemplate"
                               :project-id="projectId"
                               :preprocessing-results="preprocessingResults" @valueChanged="valueChanged"
                               :document-object="documentObject"
                               @highlightTextInViewer="highlightTextInViewer"
                               ref="eventForms"
                    ></d-t-event>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import {
    additionalDocumentPropertiesInDocInfo,
    projectIdToTemplate
} from "@/config";
import DTOverview from "@/components/DTOverview";
import DTSerial from "@/components/DTSerial";
import DTEvent from "@/components/DTEvent";
import DTSerialManager from "@/components/DTSerialManager";
import {bindDocumentToSerial, createSerial, deleteSerial, unbindSerialFromGlobalDocument} from "@/apis";
import {eventCopyHelper, tempIdProvider, userSettingsManager, workModeEdit, workModeRead} from "@/utils";
import SimpleDropdown from "@/components/SimpleDropdown";
import {Promise} from 'es6-promise-polyfill';
import $ from 'jquery';
import Vue from 'vue'
import DTEventForCU from "@/components/DTEventForCU";
import DTEventForAR from "@/components/DTEventForAR";

const documentOverviewTabId = 'docOverview';
const serialManagerTabId = 'docSerialManager';

export default {
    name: "DocumentControlPanel",
    components: {DTEventForAR, DTEventForCU, SimpleDropdown, DTSerialManager, DTEvent, DTSerial, DTOverview},
    props: {
        documentObject: Object,
        status: String,
        statusSetBy: String,
        canEdit: Boolean,
        projectId: String,
    },
    mounted() {
        this.$nextTick(() => {
            this.initialize();
        })
    },
    created() {
        this.eventObjects.forEach(eventObject => {
            eventObject['_v_key'] = tempIdProvider()
        })

        this.unbindSerialListener = (serialId) => {
            this.attemptToUnbindSerial(serialId)
        }

        this.bindSerialListener = (serialId) => {
            this.bindToSerialFromSerialManager(serialId)
        }

        this.deleteSerialListener = (serialId) => {
            this.attemptToDeleteSerial(serialId);
        }

        this.eventHub.$on('attemptToUnbindSerialFor' + this.documentObject['global_document_id'], this.unbindSerialListener);
        this.eventHub.$on('attemptToBindSerialFor' + this.documentObject['global_document_id'], this.bindSerialListener);
        this.eventHub.$on('attemptToDeleteSerialFor' + this.documentObject['global_document_id'], this.deleteSerialListener);
    },
    destroyed() {
        this.eventHub.$off('attemptToUnbindSerialFor' + this.documentObject['global_document_id'], this.unbindSerialListener);
        this.eventHub.$off('attemptToBindSerialFor' + this.documentObject['global_document_id'], this.bindSerialListener);
        this.eventHub.$off('attemptToDeleteSerialFor' + this.documentObject['global_document_id'], this.deleteSerialListener);
    },
    computed: {
        documentDescriptionItems() {
            const docObj = this.documentObject;

            const items = [
                {
                    label: 'Document ID',
                    value: docObj['document_id']
                },
            ];

            let rendered;

            additionalDocumentPropertiesInDocInfo.forEach(value => {
                rendered = value.renderer(docObj);
                if (rendered) {
                    items.push({
                        label: value.label,
                        value: rendered,
                    })
                }
            });

            return items;
        },
        serialObjects() {
            if (this.enableSerial) {
                return this.documentObject['serials'];
            } else {
                return null;
            }
        },
        eventObjects() {
            if (this.enableSerial) {
                const eventObjects = [];
                this.serialObjects.forEach(serialObject => {
                    (serialObject['events'] || []).forEach(eventObject => {
                        eventObjects.push(eventObject);
                    })
                });
                return eventObjects;
            } else {
                return this.documentObject['events'];
            }
        },
        projectFieldsTemplate() {
            return projectIdToTemplate[this.documentObject['project_id']];
        },
        preprocessingResults() {
            return this.documentObject['preprocessing_results'];
        },
        workMode() {
            return userSettingsManager.getWorkMode();
        },
        inEditWorkMode() {
            return this.workMode === workModeEdit;
        },
        inReadWorkMode() {
            return this.workMode === workModeRead;
        },
        enableSerial() {
            const {projectTemplate} = this;
            return projectTemplate && projectTemplate['enable_serial'];
        },
        maxEvents() {
            const {projectTemplate} = this;
            return projectTemplate && projectTemplate['max_events']
        },
        projectTemplate() {
            return projectIdToTemplate[this.projectId]
        },
        isCapacityUpdater() {
            return this.projectId === '12';
        },
        isAnnualReport() {
            return this.projectId === '26';
        }
    },
    data() {
        return {
            documentOverviewTabId,
            serialManagerTabId,
            activeTabId: documentOverviewTabId,
            solidSavableObject: null,
            unbindSerialListener: null,
            bindSerialListener: null,
            deleteSerialListener: null,
            tabComponentScrollTopMaps: {},
            initialized: false,
        }
    },
    methods: {
        vScrollItemsBarToBottom() {
            const {itemsBar} = this.$refs;
            itemsBar.scrollTop = itemsBar.scrollHeight;
        },
        initialize() {
            const promises = [];

            if (this.eventObjects.length) {
                this.$refs.eventForms.forEach((form) => {
                    promises.push(form.initializedPromise)
                });
            }

            Promise.all(promises).then(() => {
                this.setSolidSavableObject(this.buildCurrentSavableObject());
                this.initialized = true;
            })
        },
        copyEventValues(eventId) {
            const projectId = this.documentObject['project_id'];
            const eventForm = this.getEventFormWith(eventId);
            eventCopyHelper.setCopyObj({
                projectId,
                formData: eventForm.getFormValues()
            })
        },
        overwriteEventValues(eventId) {
            const cProjectId = this.documentObject['project_id'];
            const eventForm = this.getEventFormWith(eventId);

            const obj = eventCopyHelper.getCopyObj();
            if (!obj) {
                alert('It seems you haven\'t copied an event!')
                return
            }
            const {projectId, formData} = obj

            if (projectId !== cProjectId) {
                alert('It seems the event you copied is from a different project!')
                return;
            }

            eventForm.resetForm(JSON.parse(JSON.stringify(formData)));
        },
        eventNewlyCreated(eventObject) {
            return eventObject['event_id'][0] === ':';
        },
        highlightTextInViewer(text) {
            this.$emit('highlightTextInViewer', text);
        },
        changeEventIdsWithMaps(map) {
            this.eventObjects.forEach(v => {
                const old = v['event_id'];
                if (map.hasOwnProperty(old)) {
                    v['event_id'] = map[old];
                }
            });

            if (map.hasOwnProperty(this.activeTabId)) {
                this.activeTabId = map[this.activeTabId];
            }
        },
        updateEventsUserRelatedDataWithMaps(map) {
            let eventId;
            let data;

            this.eventObjects.forEach(v => {
                eventId = v['event_id'];
                if (map.hasOwnProperty(eventId)) {
                    data = map[eventId];
                    Vue.set(v, 'last_update', data['last_update']);
                    Vue.set(v, 'last_update_admin', data['last_update_admin']);
                    Vue.set(v, 'create_time', data['create_time']);
                    Vue.set(v, 'created_by', data['created_by']);
                }
            });
        },
        setSolidSavableObject(newVal) {
            if (newVal === this.solidSavableObject) {
                return;
            }
            this.solidSavableObject = newVal;
        },
        valueChanged() {
            this.$emit('valueChanged');
        },
        buildCurrentSavableObject() {
            const obj = {
                status: this.status,
                statusSetBy: this.statusSetBy,
            };

            let events = [];
            let event;
            if (this.eventObjects.length) {
                this.$refs.eventForms.forEach((form) => {
                    event = {
                        'event_id': form.getEventId(),
                        'values': form.getFormValues()
                    };
                    if (this.enableSerial) {
                        event['serial_id'] = form.getSerialId()
                    }
                    events.push(event);
                });
            }
            obj.events = events;

            return obj;
        },
        getSolidSavableObject() {
            return this.solidSavableObject;
        },
        setActiveTabId(tabId) {
            if (this.activeTabId === tabId) {
                return;
            }

            this.tabComponentScrollTopMaps[this.activeTabId] = this.$refs.tabContentContainer.scrollTop;

            this.activeTabId = tabId;

            this.$nextTick(() => {
                this.$refs.tabContentContainer.scrollTop = this.tabComponentScrollTopMaps[this.activeTabId] || 0;

                if (tabId === serialManagerTabId) {
                    this.$refs.serialManager.focus();
                }
            })
        },
        createSerialAndBind() {
            const projectId = this.documentObject['project_id'];
            const globalDocumentId = this.documentObject['global_document_id'];

            let createdSerialId;

            createSerial(projectId).then((r) => {
                createdSerialId = r['serial_id'];
                return bindDocumentToSerial(globalDocumentId, createdSerialId)
            }).then(() => {
                this.vAppendSerial(createdSerialId);
                this.setActiveTabId(createdSerialId);
            }).catch((err) => {
                this.handleServerSideError(err).catch(() => {
                    alert('There was something not right with the connection with the server.');
                })
            })
        },
        attemptToDeleteSerial(serialId) {
            deleteSerial(serialId).then(r => {
                if (r['error_code'] === 1) {
                    alert('You cannot delete the serial when there are still documents bound to it!')
                } else {
                    this.resetSerialManagerResult();
                }
            }).catch(e => {
                this.handleServerSideError(e)
            })
        },
        bindToSerialFromSerialManager(serialId) {
            bindDocumentToSerial(this.documentObject['global_document_id'], serialId).then(() => {
                this.vAppendSerial(serialId);
                this.$nextTick(() => {
                    this.updateSerialManagerAndCards();
                })
            }).catch((err) => {
                this.handleServerSideError(err).catch(() => {
                    alert('There was something not right with the connection with the server.');
                })
            })
        },
        resetSerialManagerResult() {
            this.$refs.serialManager.resetInitial();
        },
        attemptToUnbindSerial(serialId) {
            let serialObject = this.serialObjects.filter(s => s['serial_id'] === serialId)[0];

            if (serialObject && serialObject['events'].length) {
                this.$nextTick(() => {
                    alert('Serial cannot be detached with references from events.');
                });
                return
            }

            const globalDocumentId = this.documentObject['global_document_id'];

            unbindSerialFromGlobalDocument(serialId, globalDocumentId).then((r) => {
                if (r['error_code'] === 1) {
                    alert('You cannot unbind the serial from the document. Submit changes and try again.')
                } else {
                    this.vUnbindSerial(serialId);

                    this.$nextTick(() => {
                        this.$refs.serialManager.reload();
                    })
                }
            })
        },
        viewInSerialManager(serialId) {
            this.setActiveTabId(serialManagerTabId);
            this.$nextTick(() => {
                this.$refs.serialManager.revealSerialId(serialId);
            })
        },
        vAppendSerial(serialId) {
            this.documentObject['serials'].push({
                'serial_id': serialId,
                'events': []
            });

            // this.$nextTick(() => {
            //     this.valueChanged();
            // });
        },
        vUnbindSerial(serialId) {
            this.documentObject['serials'] = this.documentObject['serials'].filter(v => (v['serial_id'] !== serialId));

            // this.$nextTick(() => {
            //     this.valueChanged();
            // })
        },
        vAppendEventToSerial(serialObject) {
            const vk = tempIdProvider();
            serialObject['events'].push({
                'event_id': tempIdProvider(),
                'serial_id': serialObject['serial_id'],
                '_v_key': vk
            });

            this.setActiveTabId(vk);

            this.$nextTick(() => {
                this.valueChanged();
            })
        },
        vAppendEventToEvents() {
            const vk = tempIdProvider();
            const tempEventId = tempIdProvider();
            const eventObject = {
                'event_id': tempEventId,
                '_v_key': vk
            };
            this.documentObject['events'].push(eventObject);

            this.setActiveTabId(vk);

            this.$nextTick(() => {
                this.valueChanged();
                this.vScrollItemsBarToBottom();
            });

            return eventObject;
        },
        onDeleteEventButtonClicked(vKey, serialObject) {
            if (window.confirm('Are you sure you want to delete the event?')) {
                this.vDeleteEvent(vKey, serialObject);
            }
        },
        vDeleteEvent(vKey, serialObject) {
            let newEvents = [];
            let deletedEventIndex = -1;

            if (serialObject) {
                serialObject['events'].forEach((eventObject, index) => {
                    if (eventObject['_v_key'] === vKey) {
                        deletedEventIndex = index;
                    } else {
                        newEvents.push(eventObject);
                    }
                });

                serialObject['events'] = newEvents;
            } else {
                this.eventObjects.forEach((eventObject, index) => {
                    if (eventObject['_v_key'] === vKey) {
                        deletedEventIndex = index;
                    } else {
                        newEvents.push(eventObject);
                    }
                });

                this.documentObject['events'] = newEvents;
            }

            if (this.activeTabId === vKey) {
                let nextActiveEventIndex = -1;
                if (deletedEventIndex < newEvents.length) {
                    nextActiveEventIndex = deletedEventIndex;
                } else if (deletedEventIndex - 1 < newEvents.length) {
                    nextActiveEventIndex = deletedEventIndex - 1;
                }

                if (nextActiveEventIndex >= 0) {
                    this.setActiveTabId(newEvents[nextActiveEventIndex]['_v_key']);
                } else {
                    if (serialObject) {
                        this.setActiveTabId(serialObject['serial_id']);
                    } else {
                        this.setActiveTabId(documentOverviewTabId);
                    }
                }
            }

            this.$nextTick(() => {
                this.valueChanged();
            })
        },
        vResetEventToLastSaved(eventObject) {
            const eventForms = this.$refs.eventForms;
            let thisForm;
            let obj;
            for (let i = 0; i < eventForms.length; i++) {
                thisForm = eventForms[i];
                if (thisForm.getEventId() === eventObject['event_id']) {
                    obj = this.solidSavableObject.events.filter(item => (item['event_id'] === eventObject['event_id']))[0];
                    if (obj) {
                        thisForm.resetForm(obj.values);
                        // this.setSolidSavableObject(this.buildCurrentSavableObject());
                    }
                    break
                }
            }
        },
        highlightEventAndProperty(eventId, fieldId) {
            this.ensureEventObjectExistsAndActive(eventId).then((eventObject) => {
                const eventForm = this.getEventFormWith(eventObject['event_id']);
                if (eventForm && fieldId) {
                    const elem = $(eventForm.$el).find(`.property-item[data-field-id="${fieldId}"]`).children('.property-label')[0];
                    if (elem) {
                        // if (elem.blurTimeout) {
                        //     clearTimeout(elem.blurTimeout);
                        //     elem.blurTimeout = null;
                        // }
                        elem.focus();
                        // elem.blurTimeout = setTimeout(() => {
                        //     elem.blur();
                        // }, 1500);
                    }
                }
            })
        },
        highlightSerial(serialId) {
            const s = this.getSerialObjectWith(serialId);
            if (s) {
                this.setActiveTabId(serialId);
            }
        },
        getEventFormWith(eventId) {
            const forms = this.$refs.eventForms;
            for (let i = 0; i < forms.length; i++) {
                if (forms[i].getEventId() === eventId) {
                    return forms[i];
                }
            }
            return null;
        },
        getEventObjectWith(eventId) {
            const objects = this.eventObjects;
            for (let i = 0; i < objects.length; i++) {
                if (objects[i]['event_id'] === eventId) {
                    return objects[i];
                }
            }
            return null;
        },
        getSerialObjectWith(serialId) {
            const objects = this.serialObjects;
            for (let i = 0; i < objects.length; i++) {
                if (objects[i]['serial_id'] === serialId) {
                    return objects[i];
                }
            }
            return null;
        },
        ensureEventObjectExistsAndActive(eventId) {
            return new Promise((resolve, reject) => {
                let eventObject;
                if (eventId) {
                    eventObject = this.getEventObjectWith(eventId);
                    if (eventObject) {
                        this.setActiveTabId(eventObject['_v_key']);
                        this.$nextTick(() => {
                            resolve(eventObject);
                        })
                    } else {
                        reject();
                    }
                } else if (!this.enableSerial) {
                    // this.$nextTick(() => {
                    //     eventObject = this.vAppendEventToEvents();
                    //     this.$nextTick(() => {
                    //         resolve(eventObject);
                    //     })
                    // })
                } else {
                    // todo: if enable serial and event id not exist, should prompt users to select or create a serial where the new event resides.
                }
            })
        },
        updateSerialManagerAndCards() {
            this.$refs.serialManager.reload();
            let {dtSerials} = this.$refs;
            if (dtSerials) {
                dtSerials.forEach(v => {
                    v.reload();
                })
            }
        }
    },
}
</script>

<style scoped lang="scss">
@import "../assets/variables";

.control-panel {
    background-color: white;

    .main-body {
        height: 100%;
        background-color: #f3f3f3;
        display: flex;

        .tabs-bar, .tab-content-container {
            height: 100%;
            overflow: auto;
        }

        .tabs-bar {
          width: 26%;
          background-color: darken(#f3f3f3, 5);
          min-width: 180px;
        }

        .tab-content-container {
            width: 74%;
            padding: 1.2rem 1.2rem 1.2rem;
        }
    }

    &.show-header {
        .main-body {
            height: calc(100% - 24px);
        }
    }
}

.sticky-header {
    padding: 0.5rem 0 0.5rem;
    background-color: rgba(#e6e6e6, 0.5);
    position: sticky;
    top: -0.5rem;
    z-index: 1;
    backdrop-filter: blur(5px);
}
</style>
